import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CircleQuestionMark } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BUG_REPORT_DEEP_LINK_PARAM } from '../constants';
import { ReportBugDialog } from './report-bug-dialog';

/**
 * The header "?" button. Self-contained (button + its dialog) so the app
 * header stays a layout file — same shape as NotificationBell and
 * CommandCenterTrigger, and the reason this renders identically in the
 * desktop and mobile header rows without duplicating any state.
 *
 * It is also where a bug-report notification lands. The dialog is not a route
 * (deliberately — see ReportBugDialog), so the notification's `actionUrl`
 * carries `?bugReportId=…` on whatever page the user is on, and this button —
 * mounted on every page — is what turns that into an open thread.
 */
export function ReportBugTrigger() {
    const { t } = useTranslation();
    const [manualOpen, setManualOpen] = useState(false);
    // Bumped on every "?" click so the dialog remounts with a clean form, tab
    // and attachment list — a key change instead of a pile of reset effects.
    const [sessionKey, setSessionKey] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const label = t('bugReports.trigger.label', 'Report an issue');

    // Read straight from the URL rather than copied into state by an effect:
    // there is then no render in which the param is present and the dialog is
    // still closed, and no cascading re-render to open it.
    const deepLinkedId = searchParams.get(BUG_REPORT_DEEP_LINK_PARAM) ?? undefined;

    // The deep-linked id doubles as the remount key. The dialog reads its
    // opening tab once, at mount, so arriving on a report has to produce a new
    // component — including when a second notification names a different one.
    const dialogKey = deepLinkedId ?? `manual:${String(sessionKey)}`;

    function clearDeepLink(): void {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete(BUG_REPORT_DEEP_LINK_PARAM);
                return next;
            },
            { replace: true },
        );
    }

    function handleOpen(): void {
        // Someone clicking "?" wants to file something new, even while a
        // deep-linked thread is on screen — so the param goes with the old
        // dialog rather than surviving into the new one.
        if (deepLinkedId) clearDeepLink();
        setSessionKey((key) => key + 1);
        setManualOpen(true);
    }

    function handleOpenChange(next: boolean): void {
        setManualOpen(next);
        if (!next && deepLinkedId) clearDeepLink();
    }

    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon-sm" aria-label={label} onClick={handleOpen}>
                        <CircleQuestionMark className="size-4.5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
            </Tooltip>
            <ReportBugDialog
                key={dialogKey}
                open={manualOpen || deepLinkedId !== undefined}
                onOpenChange={handleOpenChange}
                openReportId={deepLinkedId}
            />
        </>
    );
}
